import { metricKeys } from '@pulse/metrics';
import { ALL_TRAFFIC } from '@utils/filter';
import { GRANULARITY } from '@src/utils/granularity';
import { createFlowPresets } from './presetCreator/DPI/createFlowPresets';
import { createFrustrationSignalPresets } from './presetCreator/DPI/createFrustrationSignalPresets';
import { createEventPreset } from './presetCreator/DPI/createEventPreset';
import { SUMMARY_PANEL_TYPE } from '@src/components/top-widget-panel/constants';
import { getValidPresetKeys } from '@src/utils/presets/utils';
import { getFeature } from '@globalStore/featureStore';
import { metricsStore } from '@globalStore/metricsStore';
import featureNames from '@src/utils/featureToggle/constants';
import { MOB_PLATFORM, WEB_PLATFORM } from '@src/utils/filter/fixedFilter';
import { buildWidgetConfig } from '@src/components/widget-panel/state/utils';
import { ECO_WIDGET_TYPE_MAP, ECO_WIDGET_TYPES } from '@src/components/widget-panel/config/eco';
import { Tabs as MetricPanelTabs } from '@src/components/metrics-panel-hz/atoms';

const PRESET_KEYS = getValidPresetKeys();

const { metricsCategoryKeys } = metricsStore.getState();

export const getAppEventsPreset = ({ validateMetric, validateDimension }) => {
  return {
    title: 'Events',
    key: PRESET_KEYS.APP_EVENTS,
    description:
      'Preset focused on user events like Button, link clicks & more (use reset option for latest preset)',
    hide: false,
    linkedDimensionCategory: 'events',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.AppTotalEvents),
        metrics: validateMetric([metricKeys.AppTotalEvents, metricKeys.AppUniqueDevices]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
        granularity: GRANULARITY.PT5M,
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'event_category',
            'event_name',
            'button_click_text',
            'button_click_name',
            'request_target_url',
            'app_id',
          ]),
        },
      },
    },
  };
};

export const getServerSidePerformancePreset = ({ validateMetric, validateDimension }) => {
  return {
    title: 'Service Performance',
    key: PRESET_KEYS.APP_NETWORK_PERFORMANCE,
    description: 'Preset focused on Network/HTTP requests & responses',
    hide: false,
    linkedMetricCategory: metricsCategoryKeys.ECO_SERVICE_PERFORMANCE,
    linkedDimensionCategory: 'network',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.AverageRequestDuration),
        metrics: validateMetric([
          metricKeys.FailedNetworkRequest,
          metricKeys.AverageRequestDuration,
          metricKeys.AverageRequestCount,
          metricKeys.AppSessions,
          metricKeys.user_session_count,
          metricKeys.mins_with_0_nwr_failure,
          metricKeys.mins_with_4xx_nwr_failure,
          metricKeys.mins_with_5xx_nwr_failure,
          metricKeys.mins_with_high_nwr_duration,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
        granularity: GRANULARITY.PT5M,
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'network_request_response_code',
            'network_request_url_host',
            'network_request_url_path',
            'page_title',
            'screen_title',
            'device_name',
            'application_version',
          ]),
          globalOrderBy: validateMetric(metricKeys.AverageRequestCount),
          dimensionMetrics: validateMetric([
            metricKeys.AverageRequestCount,
            metricKeys.AverageRequestDuration,
            metricKeys.FailedNetworkRequest,
          ]),
        },
      },
      summaryWidgets: {
        type: SUMMARY_PANEL_TYPE.NETWORK,
        widgets: [],
      },
    },
  };
};

export const getMobileExperiencePreset = ({ validateMetric, validateDimension }) => {
  const widgets = {};
  const disableMobilePreset = getFeature(featureNames.DPI_EXCLUDE_MOBILE_META);
  const mobilePresetWidgets = [
    {
      ...ECO_WIDGET_TYPE_MAP[ECO_WIDGET_TYPES.metricGroup],
      state: {
        groupName: 'Audience',
        metrics: [
          metricKeys.AppSessions,
          metricKeys.UserActiveTime,
          metricKeys.AppSubscribers,
          metricKeys.AppUniqueDevices,
        ],
      },
    },
    {
      ...ECO_WIDGET_TYPE_MAP[ECO_WIDGET_TYPES.crashes],
      state: {
        groupName: 'Crashes ',
        timeseriesMetrics: [metricKeys.AppCrashSessions, metricKeys.AppUniqueDevices],
        donutChartMetrics: [
          metricKeys.app_crash_to_session_end_success,
          metricKeys.crash_to_app_startup_success,
        ],
      },
    },
    {
      ...ECO_WIDGET_TYPE_MAP[ECO_WIDGET_TYPES.anr],
      state: {
        groupName: 'ANR',
        timeseriesMetrics: [metricKeys.anr_count, metricKeys.AppUniqueDevices],
        donutChartMetrics: [
          metricKeys.anr_to_session_end_success,
          metricKeys.anr_to_app_startup_success,
        ],
      },
    },
  ];

  mobilePresetWidgets.forEach((widget) => {
    const widgetConfig = buildWidgetConfig({ type: widget.type, state: widget.state });
    widgets[widgetConfig.wid] = widgetConfig;
  });

  return {
    title: 'Mobile Experience',
    key: PRESET_KEYS.APP_MOBILE_EXPERIENCE,
    description: 'Preset for App metrics and dimensions including Crashes & ANRs',
    hide: disableMobilePreset,
    linkedMetricCategory: metricsCategoryKeys.ECO_APP_USER_EXPERIENCE,
    linkedDimensionCategory: 'screen',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.AppSubscribers),
        metrics: validateMetric([
          metricKeys.AppAvgScreenLoadTime,
          metricKeys.AppScreenLoads,
          metricKeys.AppStartupTime,
          metricKeys.AppStartupCount,
          metricKeys.user_session_count_ended,
          metricKeys.user_session_active_time_ended,
          metricKeys.AppSubscribers,
          metricKeys.AppCrashSessions,
          metricKeys.anr_count,
        ]),
      },
      atomState: {
        metricPanel: {
          percentile: 'p90', // Set default aggregation to P90
        },
      },
      common: {
        appliedFilters: [MOB_PLATFORM],
        granularity: GRANULARITY.PT5M,
      },
      'module:trends': {
        base: {
          dimensions: validateDimension(['app_id', 'application_version', 'screen_title']),
        },
      },
      widgetState: {
        widgets,
      },
      metricPanel: {
        widgetPanelVisibility: true,
        activeTab: MetricPanelTabs.Widgets,
      },
    },
  };
};

export const getWebExperiencePreset = ({ validateMetric, validateDimension }) => {
  return {
    title: 'Web Experience',
    key: PRESET_KEYS.APP_WEB_EXPERIENCE,
    description: 'Preset for Web Page metrics and dimensions',
    hide: false,
    linkedMetricCategory: metricsCategoryKeys.ECO_WEB_USER_EXPERIENCE,
    linkedDimensionCategory: 'page',
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.DocumentLoads),
        metrics: validateMetric([
          metricKeys.avg_cvv_soft_navigation_time,
          metricKeys.DocumentLoadTime,
          metricKeys.AvgLargestContentfulPaintTime,
          metricKeys.DocumentLoads,
          metricKeys.AverageMinutesPerPage,
          metricKeys.user_session_count_ended,
          metricKeys.user_session_active_time_ended,
          metricKeys.AppSubscribers,
          metricKeys.AppErrors,
        ]),
      },
      atomState: {
        metricPanel: {
          percentile: 'p90', // Set default aggregation to P90
        },
      },
      common: {
        appliedFilters: [WEB_PLATFORM],
        granularity: GRANULARITY.PT5M,
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'page_urlhost',
            'page_urlpath',
            'page_title',
            'browser_name',
          ]),
        },
      },
    },
  };
};

export const getEngagementPreset = ({ validateMetric, validateDimension }) => {
  const audienceEngagementFeature = getFeature(featureNames.AUDIENCE_ENGAGEMENT_METRICS);
  return {
    title: 'Audience and Engagement',
    key: PRESET_KEYS.ECO_ENGAGEMENT,
    description: 'Analyze Audience and Engagement metrics',
    hide: !audienceEngagementFeature,
    config: {
      metricsChartContainer: {
        metric: validateMetric(metricKeys.active_time_per_active_devices),
        metrics: validateMetric([
          metricKeys.active_time_per_active_devices,
          metricKeys.active_time_per_active_users,
          metricKeys.active_time_per_app_sessions,
          metricKeys.active_time_per_page_views,
          metricKeys.page_views_per_active_devices,
          metricKeys.page_views_per_active_users,
          metricKeys.page_views_per_app_sessions,
          metricKeys.total_events_per_active_devices,
          metricKeys.total_events_per_active_users,
          metricKeys.total_events_per_app_sessions,
          metricKeys.total_events_per_page_views,
        ]),
      },
      common: {
        appliedFilters: [ALL_TRAFFIC],
        granularity: GRANULARITY.P1D,
      },
      'module:trends': {
        base: {
          dimensions: validateDimension([
            'device_name',
            'application_version',
            'page_urlpath',
            'geo_country',
            'app_id',
          ]),
        },
      },
    },
  };
};

export const genAppUsecases = ({ validateMetric, validateDimension }) => ({
  [PRESET_KEYS.APP_WEB_EXPERIENCE]: getWebExperiencePreset({ validateMetric, validateDimension }),
  [PRESET_KEYS.APP_MOBILE_EXPERIENCE]: getMobileExperiencePreset({
    validateMetric,
    validateDimension,
  }),
  [PRESET_KEYS.APP_NETWORK_PERFORMANCE]: getServerSidePerformancePreset({
    validateMetric,
    validateDimension,
  }),
  [PRESET_KEYS.APP_EVENTS]: getAppEventsPreset({ validateMetric, validateDimension }),
  [PRESET_KEYS.ECO_ENGAGEMENT]: getEngagementPreset({ validateMetric, validateDimension }),
});

export const generateEcoFlowUsecases = ({ metrics, validateDimension, flowConfigMeta }) =>
  createFlowPresets({ metrics, validateDimension, flowConfigMeta });

export const generateEcoFrustrationSignalUsecases = ({
  metrics,
  validateDimension,
  frustrationSignalConfigMeta,
}) => createFrustrationSignalPresets({ metrics, validateDimension, frustrationSignalConfigMeta });

export const generateEcoEventUsecase = ({ metrics, validateDimension, eventConfigMeta }) =>
  createEventPreset({ metrics, validateDimension, eventConfigMeta });
